import { useEffect, useState } from 'react';
import { FaCheck, FaPen, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';

import { createLeague, deleteLeague, getLeagues, getTeamsForLeague, updateLeague, updateLeagueOrder } from '../api';
import { generateLeagueTheme } from '../utils/colorUtils';

function LeaguesScreen() {
  const [leagues, setLeagues] = useState([]);
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newLeagueRounds, setNewLeagueRounds] = useState(1);
  const [editingLeague, setEditingLeague] = useState(null);

  // State for the delete confirmation dialog.
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState(null);
  const [teamsInLeague, setTeamsInLeague] = useState([]);
  const [deleteTeamsCheckbox, setDeleteTeamsCheckbox] = useState(false);

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    const response = await getLeagues();
    setLeagues(response.data);
  };

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    await createLeague({ name: newLeagueName, rounds: newLeagueRounds });
    setNewLeagueName('');
    setNewLeagueRounds(1);
    fetchLeagues();
  };

  const handleUpdateLeague = async (id) => {
    await updateLeague(id, { name: editingLeague.name, rounds: editingLeague.rounds });
    setEditingLeague(null);
    fetchLeagues();
  };

  // Step 1: Open the dialog when a user clicks "Delete".
  const handleDeleteLeague = async (league) => {
    setLeagueToDelete(league);
    const response = await getTeamsForLeague(league.id);
    setTeamsInLeague(response.data);
    setShowDeleteDialog(true);
  };

  // Step 2: Handle the actual deletion after confirmation.
  const confirmDelete = async () => {
    if (!leagueToDelete) return;

    // The backend will now handle the cascading delete based on the query parameter.
    await deleteLeague(leagueToDelete.id, deleteTeamsCheckbox);
    
    // Reset state and close dialog.
    setShowDeleteDialog(false);
    setLeagueToDelete(null);
    setTeamsInLeague([]);
    setDeleteTeamsCheckbox(false);
    fetchLeagues();
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setLeagueToDelete(null);
    setTeamsInLeague([]);
    setDeleteTeamsCheckbox(false);
  };

  const handleMove = async (index, direction) => {
    const newLeagues = [...leagues];
    const [movedLeague] = newLeagues.splice(index, 1);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    newLeagues.splice(newIndex, 0, movedLeague);
    setLeagues(newLeagues);
    const leagueIds = newLeagues.map(league => league.id);
    await updateLeagueOrder(leagueIds);
  };

  return (
    <>
      {/* Delete Confirmation Dialog. */}
      {showDeleteDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete the league: <strong>{leagueToDelete?.name}</strong>?</p>
            {teamsInLeague.length > 0 && (
              <div>
                <p>This league contains the following teams:</p>
                <ul>
                  {teamsInLeague.map(team => <li key={team.id}>{team.name}</li>)}
                </ul>
                <label>
                  <input
                    type="checkbox"
                    checked={deleteTeamsCheckbox}
                    onChange={(e) => setDeleteTeamsCheckbox(e.target.checked)}
                  />
                  Also delete all teams in this league.
                </label>
              </div>
            )}
            <div className="dialog-actions">
              <button onClick={cancelDelete}>Cancel</button>
              <button onClick={confirmDelete} className="danger">Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>Leagues</h2>
        </div>

        <form onSubmit={handleCreateLeague}>
          <div className="form-group">
            <label htmlFor="league-name">League Name</label>
            <input
              id="league-name"
              type="text"
              placeholder="Enter league name"
              value={newLeagueName}
              onChange={(e) => setNewLeagueName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="league-rounds">Rounds</label>
            <div className="select-container">
              <select
                id="league-rounds"
                value={newLeagueRounds}
                onChange={(e) => setNewLeagueRounds(parseInt(e.target.value, 10))}
              >
                <option value={1}>Single round</option>
                <option value={2}>Double (home and away)</option>
                <option value={3}>Triple</option>
                <option value={4}>Quadruple</option>
              </select>
            </div>
          </div>
          <button type="submit">Create League</button>
        </form>

        <table>
        <thead>
          <tr>
            <th>League</th>
            <th>Rounds</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {leagues.map((league, index) => {
            const themeColors = generateLeagueTheme(league.name);
            return (
            <tr
              key={league.id}
              className="league-row"
              style={{
                borderLeft: `4px solid ${themeColors.dark}`,
              }}
            >
              <td>
                {editingLeague && editingLeague.id === league.id ? (
                  <input
                    type="text"
                    value={editingLeague.name}
                    onChange={(e) => setEditingLeague({ ...editingLeague, name: e.target.value })}
                  />
                ) : (
                  league.name
                )}
              </td>
              <td>
                {editingLeague && editingLeague.id === league.id ? (
                  <div className="select-container">
                    <select
                      value={editingLeague.rounds}
                      onChange={(e) => setEditingLeague({ ...editingLeague, rounds: parseInt(e.target.value, 10) })}
                    >
                      <option value={1}>Single</option>
                      <option value={2}>Double</option>
                      <option value={3}>Triple</option>
                      <option value={4}>Quadruple</option>
                    </select>
                  </div>
                ) : (
                  league.rounds
                )}
              </td>
              <td>
                <div className="table-actions">
                  {editingLeague && editingLeague.id === league.id ? (
                    <button onClick={() => handleUpdateLeague(league.id)} className="submit icon-btn"><FaCheck /></button>
                  ) : (
                    <button onClick={() => setEditingLeague({ ...league })} className="icon-btn"><FaPen /></button>
                  )}
                  <button onClick={() => handleDeleteLeague(league)} className="danger icon-btn"><FaTrash /></button>
                  <button onClick={() => handleMove(index, 'up')} disabled={index === 0} className="icon-btn"><FaArrowUp /></button>
                  <button onClick={() => handleMove(index, 'down')} disabled={index === leagues.length - 1} className="icon-btn"><FaArrowDown /></button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}

export default LeaguesScreen;
